import { FC, useEffect, useMemo } from 'react';
import { atom, useAtomValue } from 'jotai';
import { Source, Layer, type MapRef } from 'react-map-gl';
import bbox from '@turf/bbox';
import { SkeletonPlaceholder } from '@carbon/react';

import { Map as MapComponent } from 'components/map';
import { WellMapLayer } from 'components/well-page-content/well-map-layer';
import { mapColors } from 'components/map/map-colors';
import type { WellboreMapData, IntersectingUnit } from 'types/api-responses';

import {
  afeMapConfigAtom,
  wellQueryLoadableAtom,
  afeWellIntersectionLoadableAtom,
} from '../atoms';
import styles from './afe-well-map.module.scss';

// Default center: geographic center of the contiguous US
const US_CENTER_LONGITUDE = -98.5795;
const US_CENTER_LATITUDE = 39.8283;
const US_ZOOM = 4;
const WELL_MAX_ZOOM = 14;

const afeWellMapRefAtom = atom<MapRef | null>(null);

interface AfeWellMapProps {
  /** When falsy, the map shows a default US view with no well overlays. */
  uwi: string | null;
}

/**
 * Read-only well map panel for the AFE wizard.
 * Renders unit polygons, producing wellbores, and sidetracks — matching
 * the wells page display (without scoped-layer toggles).
 * When no UWI is provided, shows a default US-centered map.
 * @param {AfeWellMapProps} props - UWI controlling well data display
 * @return {JSX.Element} Map panel or loading skeleton
 */
const AfeWellMap: FC<AfeWellMapProps> = ({ uwi }) => {
  const wellMapLoadable = useAtomValue(wellQueryLoadableAtom);
  const intersectionLoadable = useAtomValue(afeWellIntersectionLoadableAtom);

  const showWellData = !!uwi;

  const wellbores: WellboreMapData[] = useMemo(() => {
    if (!showWellData) return [];
    if (wellMapLoadable.state !== 'hasData' || !wellMapLoadable.data) return [];
    const well = wellMapLoadable.data.results?.[0];
    if (!well) return [];
    const wellBores = well.wellBores ?? [];
    if (wellBores.length > 0) return wellBores;
    // Permit / pre-drill wells have no drilled trajectory; their only
    // geometry is the well-level surface hole, so plot that instead.
    return well.surfaceHole
      ? [
          {
            uwi14: well.uwi,
            uwi12: well.uwi,
            type: 'producing' as const,
            surfaceHole: well.surfaceHole,
          },
        ]
      : [];
  }, [showWellData, wellMapLoadable]);

  const producingWellbores = wellbores.filter((wb) => wb.type === 'producing');
  const sidetrackWellbores = wellbores.filter((wb) => wb.type === 'sidetrack');

  const intersectingUnits: IntersectingUnit[] = useMemo(() => {
    if (!showWellData) return [];
    if (intersectionLoadable.state !== 'hasData' || !intersectionLoadable.data)
      return [];
    return intersectionLoadable.data.intersectingUnits;
  }, [showWellData, intersectionLoadable]);

  // Bounds enclosing the well geometry (survey lines + surface holes) and any
  // intersecting units, as the [SW, NE] corners fitBounds expects.
  const selectedBounds = useMemo(() => {
    if (!showWellData) return null;
    const features: GeoJSON.Feature[] = [];
    intersectingUnits.forEach((unit) => {
      const geom = (unit.geojson as { geometry?: GeoJSON.Geometry } | null)
        ?.geometry;
      if (geom)
        features.push({ type: 'Feature', properties: {}, geometry: geom });
    });
    wellbores.forEach((wb) => {
      if (wb.survey?.geometry) features.push(wb.survey);
      if (wb.surfaceHole?.coordinates) {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: wb.surfaceHole,
        });
      }
    });
    if (features.length === 0) return null;
    let computed: ReturnType<typeof bbox>;
    try {
      computed = bbox({ type: 'FeatureCollection', features });
    } catch {
      return null;
    }
    const [minLng, minLat, maxLng, maxLat] = computed;
    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ] as [[number, number], [number, number]];
  }, [showWellData, intersectingUnits, wellbores]);

  // Zoom the map to the well once its geometry loads. fitBounds centers on a
  // single surface hole at maxZoom, so no single-point special case is needed.
  const mapRef = useAtomValue(afeWellMapRefAtom);
  useEffect(() => {
    if (!mapRef || !selectedBounds) return;
    mapRef
      .getMap()
      ?.fitBounds(selectedBounds, { padding: 60, maxZoom: WELL_MAX_ZOOM });
  }, [mapRef, selectedBounds]);

  // Loading overlay while fetching well data
  if (showWellData && wellMapLoadable.state === 'loading') {
    return <SkeletonPlaceholder className={styles.skeleton} />;
  }

  return (
    <div className={styles.mapContainer}>
      <MapComponent
        latitude={US_CENTER_LATITUDE}
        longitude={US_CENTER_LONGITUDE}
        zoom={US_ZOOM}
        showControls
        showMapFilters
        blockPopups
        disableWellsLayer
        mapConfigAtom={afeMapConfigAtom}
        refAtom={afeWellMapRefAtom}
        className={styles.map}
      >
        {/* Unit polygons (assigned = cyan, non-assigned = gray) */}
        {intersectingUnits.map((unit) => {
          if (!unit.geojson) return null;
          return (
            <Source
              key={`afe-unit-${unit.id}`}
              type="geojson"
              data={unit.geojson as GeoJSON.Feature}
            >
              <Layer
                id={`afe-unit-fill-${unit.id}`}
                type="fill"
                paint={{
                  'fill-color': unit.assignedToUnit
                    ? mapColors.assignedUnitFill
                    : mapColors.nonAssignedUnitFill,
                  'fill-opacity': unit.assignedToUnit ? 0.6 : 0.4,
                }}
              />
              <Layer
                id={`afe-unit-outline-${unit.id}`}
                type="line"
                paint={{
                  'line-color': unit.assignedToUnit
                    ? mapColors.assignedUnitBorder
                    : mapColors.nonAssignedUnitBorder,
                  'line-width': 2,
                  'line-opacity': 1,
                }}
              />
            </Source>
          );
        })}

        {/* Producing wellbores */}
        {producingWellbores.map((wb, idx) => {
          const wbId = wb.uwi14 ?? `idx-${idx}`;
          return (
            <WellMapLayer
              key={`producing-${wbId}`}
              id={`producing-${wbId}`}
              wellbore={wb}
              mapConfigAtom={afeMapConfigAtom}
            />
          );
        })}

        {/* Sidetrack wellbores */}
        {sidetrackWellbores.map((wb, idx) => {
          const wbId = wb.uwi14 ?? `idx-${idx}`;
          return (
            <WellMapLayer
              key={`sidetrack-${wbId}`}
              id={`sidetrack-${wbId}`}
              wellbore={wb}
              mapConfigAtom={afeMapConfigAtom}
            />
          );
        })}
      </MapComponent>
    </div>
  );
};

export { AfeWellMap };
